import {useQuery} from '@apollo/client';
import {Box} from '@mui/material';
import React, {useMemo, useState} from 'react';
import Loading from '../../components/Loading/Loading';
import {useAccountContext} from '../../contexts/AccountContext';
import {useReviewContext} from '../../contexts/ReviewContext';
import {
  GetHandoverNotes,
  GetHandoverNotesVariables,
} from '../../schema-types/GetHandoverNotes';
import validateUUID from '../../utils/validateUUID';
import {GET_HANDOVER_NOTES_QUERY} from '../../features/notes/notesQueries';
import {
  FormikNotesValues,
  HandoverNoteFields,
  serverToEditingNotes,
  useOnUpdateNoteMutation,
} from './HandoverNotesHelpers';
import {HandoverNotesForm} from './HandoverNotesInner';
import {ErrorPage} from '../ErrorPage';

export const useHandoverNotesData = () => {
  const {reviewDetails} = useReviewContext();
  const {accountId, account} = useAccountContext();
  const {data, loading} = useQuery<GetHandoverNotes, GetHandoverNotesVariables>(
    GET_HANDOVER_NOTES_QUERY,
    {
      variables: {
        keyId: 'review_id',
        valueId: validateUUID(reviewDetails?.id) ?? 'no review ID',
        accountId: validateUUID(accountId)!,
        contactId: validateUUID(reviewDetails?.contact?.id),
      },
      skip: !reviewDetails,
    }
  );

  const initialData = useMemo(() => {
    if (!data?.reviewDetails) {
      return null;
    }
    const notes = data?.reviewDetails?.notes;
    if (!notes) {
      throw Error('No notes found for review');
    }
    const editing: FormikNotesValues = {} as any;
    notes.forEach((n) => {
      const noteName = n!.displayName! as HandoverNoteFields;
      editing[noteName] = serverToEditingNotes(n!);
    });
    return editing;
  }, [data]);

  return {
    initialData,
    loading,
    data,
  };
};

export const HandoverNotes: React.FC<{readOnly?: boolean; reviewId: string}> = ({
  readOnly = false,
  reviewId,
}) => {
  const {account} = useAccountContext();
  const {data, loading, initialData} = useHandoverNotesData();

  const detailsType: HandoverNoteFields = account?.isTrust
    ? 'General details'
    : 'Personal details';

  const [currentNotesType, setCurrentNotesType] =
    useState<HandoverNoteFields>(detailsType);
  const [refreshKey, setRefreshKey] = useState(0);

  const {_onUpdateNote} = useOnUpdateNoteMutation();

  if (loading) {
    return <Loading entityName="notes" />;
  }
  if (!initialData || !data) {
    return <ErrorPage code="404" text="Notes not found" />;
  }

  return (
    <Box>
      <HandoverNotesForm
        key={refreshKey}
        reviewId={reviewId}
        readOnly={readOnly}
        _onUpdateNote={_onUpdateNote}
        currentNotesType={currentNotesType}
        setCurrentNotesType={setCurrentNotesType}
        handoverData={data}
        initialValueOverrides={initialData}
        formFields={[]}
        incrementRefreshKey={() => setRefreshKey((prev) => prev + 1)}
      />
    </Box>
  );
};
